import { STATES_LOADING, useLoading } from "app/custom-hooks/loadingHook";
import widgetResource from "app/common/resources/widgetResource";
import { apiHelper } from "app/common/services/apiHelper";
import { swSettings } from "app/common/services/swSettings";
import { SpinnerLoading } from "app/components/spinner-loading/SpinnerLoading";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { i18nFilter } from "app/filters/i18nFilter";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import _ from "lodash";
import styled from "styled-components";
import { useEffect } from "react";
import type { EngagementOverviewKpiTableProps } from "app/pages/website-performance/components/EngagementOverviewKpi/EngagementOverviewKpiTable";
import { EngagementOverviewKpiTable } from "app/pages/website-performance/components/EngagementOverviewKpi/EngagementOverviewKpiTable";
import { useSelector } from "react-redux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { BasicDurations } from "app/services/DurationService";

const Container = styled.div`
    position: relative;

    .swWidget-frame {
        height: 290px;
    }

    ${SpinnerLoading} {
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        position: absolute;
    }
`;

type EngagementOverviewKpiProps = Pick<EngagementOverviewKpiTableProps, "getCtaLink" | "unlockHook" | "upsellButton">;

export const EngagementOverviewKpi = ({ getCtaLink, unlockHook = null, upsellButton }: EngagementOverviewKpiProps) => {
    const params = proNavigator.getParams();
    const [state, ops] = useLoading();
    const showGAApprovedData = useSelector((state: any) => state.common?.showGAApprovedData ?? false);
    const isWWOApiController = params.country === "999" && !swSettings.allowedCountry(params.country, "WebAnalysis");
    const ctrl = isWWOApiController ? "WorldwideOverview" : "WebsiteOverview";
    const duration = ctrl === "WorldwideOverview" ? BasicDurations.LAST_THREE_MONTHS : params.duration;
    useEffect(() => {
        ops.load(async () => {
            const resource = widgetResource.resourceByController(ctrl, "EngagementOverview");
            const apiParams = {
                ...params,
                includeSubDomains: params.isWWW === "*",
                timeGranularity: params.duration === "28d" ? "Daily" : "Monthly",
                keys: params.key,
                ShouldGetVerifiedData: showGAApprovedData,
                duration,
            } as any;
            delete apiParams.isWWW;
            delete apiParams.key;
            delete apiParams["#"];
            const response = await resource.Table(apiHelper.transformParamsForAPI(apiParams));
            const data = response?.Data?.find((row: { Source?: string }) => row.Source === params.webSource);
            if (data) {
                mergeGAVerifiedFlag({ ...apiParams, metric: "EngagementOverview" }, data)(response);
            }
            return data;
        });
    }, [showGAApprovedData]);
    const paramsForComponent = {
        DedupPermission: !swSettings.components.WebDedup.resources.IsDisabled,
        uniqueVisitorsPermission: !swSettings.components.UniqueVisitors.IsDisabled,
        duration,
        country: params.country,
        metric: "EngagementOverview",
    };
    const showTable = state.state === STATES_LOADING.SUCCESS && Boolean(state.data);
    const showNoData = Boolean(state.error) || (state.state === STATES_LOADING.SUCCESS && !state.data);

    return (
        <Container data-automation="engagement-overview-kpi-widget">
            <div className="swWidget swWidget--top swWidget--boxShadow">
                <div className="swWidget-frame  swWidget-frame--noFrame">
                    {state.state === STATES_LOADING.LOADING ? <SpinnerLoading /> : null}
                    {showTable ? (
                        <EngagementOverviewKpiTable
                            data={state.data}
                            params={paramsForComponent}
                            getCtaLink={getCtaLink}
                            unlockHook={unlockHook}
                            upsellButton={upsellButton}
                        />
                    ) : null}
                    {showNoData ? (
                        <TableNoData
                            messageTitle={i18nFilter()("global.nodata.notavilable")}
                            messageSubtitle=""
                        />
                    ) : null}
                </div>
            </div>
        </Container>
    );
};

const mergeGAVerifiedFlag =
    (params, data: any = {}) =>
    (response, dataRecords?) => {
        const keys = params.keys.split(",");
        const legendItems = keys.map((website) => ({
            id: website,
            name: website,
        }));
        if (!response.KeysDataVerification) {
            return;
        }

        const _mainKey = keys[0];

        //Add isGAVerified flag on the widget.data object if at least one key is verified.

        data.isGAVerified = false;
        for (let i = 0; i < keys.length; i++) {
            if (response.KeysDataVerification[keys[i]]) {
                data.isGAVerified = true;
                break;
            }
        }

        //Add isGAPrivate flag on the widget.data object only if the first key us Private.
        const swConnectedAccountsService = connectedAccountsServiceFactory(proNavigator);
        data.isGAPrivate = swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(_mainKey);

        if (keys.length > 1) {
            //Compare mode - add isGAVerified flag for each of the widget.key objects and widget.legendItem objects.
            _.each(response.KeysDataVerification, (isGAVerified, domain) => {
                const _dataObj: any =
                    _.find(dataRecords, { Domain: domain }) ||
                    response.Data[domain] ||
                    _.find(response.Data, { Domain: domain });

                //Add isGAVerified falg to the widget's data object.
                if (_dataObj) {
                    _dataObj.isGAVerified = isGAVerified;
                    _dataObj.isGAPrivate =
                        swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(domain);
                }

                //Add isGAVerified to the widget's legend object.
                const _legendObj: any = legendItems[domain] || _.find(legendItems, { name: domain });
                if (_legendObj) {
                    _legendObj.isGAVerified = isGAVerified;
                    _legendObj.isGAPrivate =
                        swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(domain);
                    _legendObj.metric = params.metric;
                }
            });
        }
    };
